import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface RAGSummary {
  green: number;
  amber: number;
  red: number;
  offline: number;
}

interface RAGSummaryChartProps {
  summary: RAGSummary;
  isLoading?: boolean;
  className?: string;
}

type StatusKey = keyof RAGSummary;

interface StatusConfig {
  key: StatusKey;
  label: string;
  dot: string;
  filter: string;
}

const STATUSES: StatusConfig[] = [
  { key: 'green', label: 'Green', dot: 'bg-green-500', filter: 'GREEN' },
  { key: 'amber', label: 'Amber', dot: 'bg-amber-500', filter: 'AMBER' },
  { key: 'red', label: 'Red', dot: 'bg-red-500', filter: 'RED' },
  { key: 'offline', label: 'Offline', dot: 'bg-slate-400', filter: 'OFFLINE' },
];

export function RAGSummaryChart({ summary, isLoading = false, className }: RAGSummaryChartProps) {
  const navigate = useNavigate();
  const total = summary.green + summary.amber + summary.red + summary.offline;

  const handleNavigate = (filter: string) => {
    navigate(filter === 'OFFLINE' ? '/machines?isOnline=false' : `/machines?ragStatus=${filter}`);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Machine Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-[68px] animate-pulse rounded-lg border bg-muted/50" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Machine Status</CardTitle>
          <span className="text-sm text-muted-foreground">{total} total</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {STATUSES.map((s) => {
            const count = summary[s.key];
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => handleNavigate(s.filter)}
                className="group flex flex-col gap-1 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <span className={cn('h-2 w-2 rounded-full', s.dot)} />
                  {s.label}
                </span>
                <span
                  className={cn(
                    'text-2xl font-semibold tabular-nums',
                    count === 0 && 'text-muted-foreground/50'
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-full justify-center"
          onClick={() => navigate('/machines')}
        >
          View All Machines
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

export default RAGSummaryChart;
